/**
 * "/" — board dashboard.
 *  - "Board": kanban by pipeline stage (Scripting → Storyboarding → Request
 *    Review → Shooting → Editing); dragging a card to another column opens
 *    the assignee picker and creates or updates a task, and each card's "⋯"
 *    menu carries the storyboard sign-off (for whoever may approve) and
 *    Archive. With "Archived" ticked it lists the archived boards instead,
 *    each with Restore.
 *  - Views the HOST draws (core/host.ts `dashboardViews`): in the desktop app,
 *    Project and Calendar — its old Projects page, merged into this one
 *    (2026-10). The web shell has Board alone.
 *
 * The old "List" table is gone: Archive lives on the card menu, Restore on
 * the archived list, Rename on the board's own header.
 */
import {
  addDateKeyDays,
  BOARD_STATUS_LABELS,
  can,
  defaultCreateChatRoom,
  isCalendarDateKey,
  nextMondayKey,
  shortBoardName,
  shortsPlan,
  SHORTS_PER_BOARD,
  type BoardKind,
  type BoardStatus,
  type BoardSummary,
  type ChangeBoardStatusResponse,
  type CreateBoardRequest,
  type StoryboardReviewResponse,
  type TeamMember,
  type AgentAttachment,
} from "@kola/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import {
  archiveBoard,
  createBoard,
  fetchShortsCrew,
  listBoards,
  restoreBoard,
  setBoardScriptFinishDate,
  updateBoardPlan,
} from "../../core/api.js";
import { useMe } from "../../core/useMe.js";
import { DateButton } from "../../ui/DateButton.js";
import { formatDateKey, formatDateKeyWithWeekday, todayDateKey } from "../../ui/date-key.js";
import { Modal } from "../../ui/Modal.js";
import { assigneesFrom, RosterPicker } from "../../ui/RosterPicker.js";
import { hostDashboardViews, onBoardsChanged, setDocumentTitle, type HostDashboardView } from "../../core/host.js";
import { ThemeToggle } from "../../ui/ThemeToggle.js";
import { AdminLink } from "../admin/AdminLink.js";
import { NotificationsBell } from "../notifications/NotificationsBell.js";
import { ToastHost, showToast } from "../../ui/toast.js";
import { stashPendingImport } from "../agent/pending-import.js";
import { StoryboardFilePicker } from "../agent/StoryboardFilePicker.js";
import { AssignStatusDialog } from "./AssignStatusDialog.js";
import { KindBadge, KindGlyph } from "./KindBadge.js";
import { thisWeekEntries, type WeekEntry } from "./dashboard-week.js";
import { matchesKindFilter, type KindFilter } from "./shorts-card.js";
import { StoryboardGateDialog, type StoryboardGateAction } from "./StoryboardGateDialog.js";
import { StatusBoard } from "./StatusBoard.js";
import { useBoardNameField } from "./useBoardNameField.js";

/** "board", or a host view's id. */
type DashboardView = string;
const VIEW_STORAGE_KEY = "kola.dashboardView";
/** F-140: the kind filter, remembered like the view — a per-reader preference. */
const KIND_STORAGE_KEY = "kola.dashboardKind";

const KIND_FILTERS: ReadonlyArray<{ id: KindFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "long", label: "Long-form" },
  { id: "shorts", label: "Shorts" },
];

/** The remembered view, if this shell still offers it — else Board (a stored "list" included). */
function initialView(hostViews: readonly HostDashboardView[]): DashboardView {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    return stored !== null && hostViews.some((entry) => entry.id === stored) ? stored : "board";
  } catch {
    return "board";
  }
}

function initialKindFilter(): KindFilter {
  try {
    const stored = localStorage.getItem(KIND_STORAGE_KEY);
    return stored === "long" || stored === "shorts" ? stored : "all";
  } catch {
    return "all";
  }
}

export function Dashboard() {
  const navigate = useNavigate();
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The create dialog: closed, open from scratch, or open on a preset (F-141 "Plan next week"). */
  const [creating, setCreating] = useState<false | { preset: CreatePreset | null }>(false);
  const hostViews = hostDashboardViews();
  const [view, setView] = useState<DashboardView>(() => initialView(hostViews));
  const [kindFilter, setKindFilter] = useState<KindFilter>(initialKindFilter);
  /** Pending stage change, waiting on the assignee dialog. */
  const [moving, setMoving] = useState<{ board: BoardSummary; toStatus: BoardStatus } | null>(
    null,
  );
  /** Pending storyboard sign-off (F-133), waiting on the gate dialog. */
  const [signingOff, setSigningOff] = useState<{
    board: BoardSummary;
    action: StoryboardGateAction;
  } | null>(null);

  const switchView = (next: DashboardView) => {
    if (next !== "board" && !hostViews.some((entry) => entry.id === next)) return;
    // Archived boards are a Board-only list; another view shows the live ones.
    if (next !== "board") setShowArchived(false);
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // View preference only — losing it is harmless.
    }
  };

  const switchKind = (next: KindFilter) => {
    setKindFilter(next);
    try {
      localStorage.setItem(KIND_STORAGE_KEY, next);
    } catch {
      // Preference only.
    }
  };

  const refresh = useCallback(async (archived: boolean) => {
    try {
      setBoards(await listBoards(archived));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    setBoards(null);
    void refresh(showArchived);
  }, [refresh, showArchived]);

  /**
   * Re-list on focus/visibility, so boards created while this view sat idle
   * appear without a manual reload: a teammate's new board, or one the
   * rainvic-tool desktop app just created via "Send to Kola" (which imports
   * over REST — this page never hears about it otherwise).
   *
   * Deliberately SILENT — it calls `refresh` without the mount effect's
   * `setBoards(null)`, so the list updates in place instead of flashing an
   * empty "Loading…" state every time the window regains focus.
   *
   * The same silent re-list runs when the host says another of its windows
   * moved a board (host.ts `boardsChanged`) — a desktop window open beside
   * this one never takes focus from it, so focus alone would miss that.
   */
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "hidden") return;
      void refresh(showArchived);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const stopBoardsChanged = onBoardsChanged(() => void refresh(showArchived));
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      stopBoardsChanged();
    };
  }, [refresh, showArchived]);

  useEffect(() => {
    setDocumentTitle("Kola — Boards");
  }, []);

  const handleCreate = async (input: CreateBoardRequest, attachment?: AgentAttachment, sourcePath?: string) => {
    try {
      const board = await createBoard(input);
      // F-144: the file rides to the board in memory; the agent imports it
      // on first open (features/agent/pending-import.ts) — on a Short, as
      // it is and with nothing after it (F-154), and from Local Files under
      // its path and a Place video button (F-155).
      if (attachment !== undefined) stashPendingImport(board.id, attachment, { kind: board.kind, sourcePath });
      setCreating(false);
      navigate(`/b/${board.id}`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to create board");
    }
  };

  /**
   * F-137: move a board's script deadline from its card. Optimistic — the
   * chip repaints on the picker's own close animation rather than a beat
   * later when the round trip lands — and reverted by the refetch-free
   * rollback below if the server refuses, so a failed edit never leaves a
   * date on screen that nobody agreed to.
   */
  const handleSetScriptDue = async (board: BoardSummary, dateKey: string) => {
    if (dateKey === "" || dateKey === board.scriptFinishDate) return;
    const previous = board.scriptFinishDate;
    setBoards(
      (prev) =>
        prev?.map((b) => (b.id === board.id ? { ...b, scriptFinishDate: dateKey } : b)) ?? null,
    );
    try {
      const updated = await setBoardScriptFinishDate(board.id, dateKey);
      setBoards((prev) => prev?.map((b) => (b.id === board.id ? updated : b)) ?? null);
      showToast(`Script due ${formatDateKey(dateKey)} — the Scripting task follows.`);
    } catch (err) {
      setBoards(
        (prev) =>
          prev?.map((b) => (b.id === board.id ? { ...b, scriptFinishDate: previous } : b)) ?? null,
      );
      showToast(err instanceof Error ? err.message : "Couldn't move the script due date");
    }
  };

  /**
   * F-141: a Shorts week's shoot day or edit deadline moved on its card —
   * the same optimistic write as the script date.
   */
  const handleSetPlanDate = async (
    board: BoardSummary,
    field: "shootDate" | "editDueDate",
    dateKey: string,
  ) => {
    if (dateKey === "" || dateKey === board[field]) return;
    const previous = board[field];
    setBoards((prev) => prev?.map((b) => (b.id === board.id ? { ...b, [field]: dateKey } : b)) ?? null);
    try {
      const updated = await updateBoardPlan(board.id, { [field]: dateKey });
      setBoards((prev) => prev?.map((b) => (b.id === board.id ? updated : b)) ?? null);
      showToast(
        field === "shootDate"
          ? `Shoot ${formatDateKeyWithWeekday(dateKey)} — the Shooting task follows.`
          : `Edit due ${formatDateKeyWithWeekday(dateKey)} — the Editing task follows.`,
      );
    } catch (err) {
      setBoards((prev) => prev?.map((b) => (b.id === board.id ? { ...b, [field]: previous } : b)) ?? null);
      showToast(err instanceof Error ? err.message : "Couldn't move the date");
    }
  };

  const handleArchive = async (board: BoardSummary) => {
    if (!window.confirm(`Archive "${board.name}"? Collaborators will lose access until it is restored.`)) {
      return;
    }
    try {
      await archiveBoard(board.id);
      setBoards((prev) => prev?.filter((b) => b.id !== board.id) ?? null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Archive failed");
    }
  };

  const handleRestore = async (board: BoardSummary) => {
    try {
      await restoreBoard(board.id);
      setBoards((prev) => prev?.filter((b) => b.id !== board.id) ?? null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Restore failed");
    }
  };

  const handleMoved = (response: ChangeBoardStatusResponse) => {
    setMoving(null);
    setBoards((prev) =>
      prev?.map((b) => (b.id === response.board.id ? response.board : b)) ?? null,
    );
    if (response.taskCreated) {
      showToast(`Moved to ${BOARD_STATUS_LABELS[response.board.status]} — task created`);
    } else if (response.taskError !== undefined) {
      showToast("Stage changed, but creating the task failed — check the task board");
    }
  };

  /**
   * A gate call answers with the whole board, and it has almost always MOVED:
   * an approval sends it to Shooting server-side and a rejection back to
   * Storyboarding, so the card changes column rather than changing a chip.
   */
  const handleStoryboardGate = (
    action: StoryboardGateAction,
    response: StoryboardReviewResponse,
  ) => {
    setSigningOff(null);
    setBoards((prev) =>
      prev?.map((b) => (b.id === response.board.id ? response.board : b)) ?? null,
    );
    const done = STORYBOARD_GATE_DONE[action];
    if (response.autoAdvanced) {
      showToast("Storyboard approved — the board moved to Shooting");
    } else if (response.taskCreated) {
      showToast(`${done} — task updated`);
    } else if (response.taskError === "task-write-failed") {
      showToast(`${done}, but the task write failed — check the task board`);
    } else {
      showToast(done);
    }
  };

  const { me } = useMe();
  const canApprove = me !== null && can(me.role, "review.approve");
  // Archived boards are listed on Board; a host view shows the live ones.
  const effectiveView: DashboardView = showArchived ? "board" : view;
  const hostView = hostViews.find((entry) => entry.id === effectiveView) ?? null;
  // F-140: the kind filter applies to every view. Shorts boards are numerous
  // and rhythmic; a long-form producer hides them in one click, and back.
  const visibleBoards = useMemo(
    () => boards?.filter((board) => matchesKindFilter(board, kindFilter)) ?? null,
    [boards, kindFilter],
  );

  return (
    <div className={`dashboard dashboard-wide${hostView !== null ? " dashboard-host" : ""}`}>
      <div className="dashboard-header">
        <h1>Kola boards</h1>
        {/* Board alone needs no switch (the web shell); the desktop app adds
            its Project and Calendar views beside it. */}
        {hostViews.length > 0 && (
          <div className="view-switch" role="tablist" aria-label="Dashboard view">
            {[{ id: "board", label: "Board" }, ...hostViews].map((entry) => (
              <button
                key={entry.id}
                type="button"
                role="tab"
                aria-selected={effectiveView === entry.id}
                className={`view-switch-btn${effectiveView === entry.id ? " active" : ""}`}
                onClick={() => switchView(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>
        )}
        <div className="kind-filter" role="group" aria-label="Board kind">
          {KIND_FILTERS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`kind-filter-btn${kindFilter === entry.id ? " active" : ""}`}
              aria-pressed={kindFilter === entry.id}
              onClick={() => switchKind(entry.id)}
            >
              {entry.id !== "all" && <KindGlyph kind={entry.id} />}
              {entry.label}
            </button>
          ))}
        </div>
        <div className="spacer" />
        <button type="button" className="btn" onClick={() => navigate("/storyboard-lab")}>
          KOLA Lab
        </button>
        <label className="toggle">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
          />
          Archived
        </label>
        <button type="button" className="btn btn-primary" onClick={() => setCreating({ preset: null })}>
          + New board
        </button>
        {me?.role === "admin" && <AdminLink />}
        <NotificationsBell />
        <ThemeToggle />
      </div>

      {error !== null && <div className="error-note">{error}</div>}

      {hostView === null && visibleBoards === null && (
        <div className="empty-state">Loading…</div>
      )}
      {hostView === null && !showArchived && visibleBoards !== null && (
        <>
          <ThisWeekStrip boards={visibleBoards} onOpen={(boardId) => navigate(`/b/${boardId}`)} />
          <StatusBoard
            boards={visibleBoards}
            onOpen={(board) => navigate(`/b/${board.id}`)}
            onMove={(board, toStatus) => setMoving({ board, toStatus })}
            onStoryboardGate={(board, action) => setSigningOff({ board, action })}
            onArchive={(board) => void handleArchive(board)}
            onSetScriptDue={(board, dateKey) => void handleSetScriptDue(board, dateKey)}
            onSetPlanDate={(board, field, dateKey) => void handleSetPlanDate(board, field, dateKey)}
            onPlanNextWeek={(board) =>
              setCreating({
                preset: {
                  kind: "shorts",
                  shootDate: board.shootDate !== null ? addDateKeyDays(board.shootDate, 7) : nextMondayKey(todayDateKey()),
                },
              })
            }
            canApprove={canApprove}
          />
        </>
      )}

      {hostView === null && showArchived && visibleBoards !== null && (
        <ArchivedList
          boards={visibleBoards}
          emptyNote={
            boards !== null && boards.length > 0
              ? `No archived ${kindFilter === "shorts" ? "Shorts" : "long-form"} boards.`
              : "No archived boards."
          }
          onOpen={(board) => navigate(`/b/${board.id}`)}
          onRestore={(board) => void handleRestore(board)}
        />
      )}

      {hostView !== null && (
        <div className="dashboard-host-view">
          {hostView.render({
            kindFilter,
            switchView,
            openBoard: (boardId, params) => {
              const query = new URLSearchParams(params ?? {}).toString();
              navigate(`/b/${encodeURIComponent(boardId)}${query ? `?${query}` : ""}`);
            },
          })}
        </div>
      )}

      {creating !== false && (
        <CreateBoardDialog
          preset={creating.preset}
          onClose={() => setCreating(false)}
          onCreate={(input, attachment, sourcePath) => void handleCreate(input, attachment, sourcePath)}
        />
      )}
      {moving !== null && (
        <AssignStatusDialog
          board={moving.board}
          toStatus={moving.toStatus}
          onClose={() => setMoving(null)}
          onDone={handleMoved}
        />
      )}
      {signingOff !== null && (
        <StoryboardGateDialog
          board={signingOff.board}
          action={signingOff.action}
          onClose={() => setSigningOff(null)}
          onDone={(response) => handleStoryboardGate(signingOff.action, response)}
        />
      )}
      <ToastHost />
    </div>
  );
}

const STORYBOARD_GATE_DONE: Record<StoryboardGateAction, string> = {
  approve: "Storyboard approved",
  send_back: "Storyboard sent back",
};

/**
 * "New board" — first WHAT you are making (F-140), then the questions that
 * kind needs. Long-form is exactly the F-135 form: a project is a name, WHEN
 * its script is due and WHO writes it. A Short (one per board since F-143)
 * asks for ONE date, the shoot day, and draws the plan it implies (docs/11
 * §4.1).
 */
/** F-141 "Plan next week": open straight on the Shorts form, a week on. */
export interface CreatePreset {
  kind: "shorts";
  /** `YYYY-MM-DD` — the next batch's shoot day. */
  shootDate: string;
}

/** Exported for its test (CreateBoardDialog.test.tsx); the dashboard is its only caller. */
export function CreateBoardDialog({
  preset,
  onClose,
  onCreate,
}: {
  preset: CreatePreset | null;
  onClose: () => void;
  onCreate: (input: CreateBoardRequest, attachment?: AgentAttachment, sourcePath?: string) => void;
}) {
  const [kind, setKind] = useState<BoardKind | null>(preset?.kind ?? null);
  if (kind === null) {
    return (
      <Modal title="New board" className="create-board-modal" onClose={onClose}>
        <KindStep onPick={setKind} onClose={onClose} />
      </Modal>
    );
  }
  if (kind === "shorts") {
    return (
      <Modal title="New Short" className="create-board-modal" onClose={onClose}>
        <ShortForm
          initialShootDate={preset?.shootDate}
          onBack={() => setKind(null)}
          onClose={onClose}
          onCreate={onCreate}
        />
      </Modal>
    );
  }
  return (
    <Modal title="New long-form video" className="create-board-modal" onClose={onClose}>
      <LongFormForm onBack={() => setKind(null)} onClose={onClose} onCreate={onCreate} />
    </Modal>
  );
}

/** "What are you making?" — two cards, no form yet. */
function KindStep({ onPick, onClose }: { onPick: (kind: BoardKind) => void; onClose: () => void }) {
  return (
    <div className="assign-dialog create-board-form">
      <p className="assign-dialog-lead">What are you making?</p>
      <div className="create-kind-grid">
        <button type="button" className="create-kind-card" autoFocus onClick={() => onPick("long")}>
          <span className="create-kind-glyph" aria-hidden>
            ▭
          </span>
          <span className="create-kind-title">Long-form video</span>
          <span className="create-kind-desc">One video, one script, one release.</span>
        </button>
        <button type="button" className="create-kind-card" onClick={() => onPick("shorts")}>
          <span className="create-kind-glyph" aria-hidden>
            ▯
          </span>
          <span className="create-kind-title">Short</span>
          <span className="create-kind-desc">One vertical Short: shot on one day, released the week after.</span>
        </button>
      </div>
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Who owns the Scripting stage — shared by both forms. F-142: a Shorts week
 * opens with the standing crew's writers already picked (a crew that cannot
 * be read, or has none, leaves the picker blank as before); a long-form
 * board asks from scratch, as it always has.
 */
function useWriters(prefill: "shorts" | null = null) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [prefilled, setPrefilled] = useState(false);
  const touched = useRef(false);
  useEffect(() => {
    if (prefill !== "shorts") return;
    let cancelled = false;
    fetchShortsCrew()
      .then((crew) => {
        if (cancelled || touched.current || crew.writers.length === 0) return;
        setSelected(new Set(crew.writers.map((a) => a.uid)));
        setPrefilled(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [prefill]);
  const toggle = (uid: string) => {
    touched.current = true;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };
  return { team, setTeam, selected, toggle, prefilled };
}

/**
 * F-146: whether the board opens a Team Chat channel named after it. Shared
 * by both forms; each starts it from `defaultCreateChatRoom(kind)`.
 */
function ChatRoomCheckbox({ checked, onChange }: { checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <label className="create-board-check">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      Create a chat room for this board
    </label>
  );
}

/**
 * The F-135 form: Create stays disabled until name, script due and at least
 * one writer are there, and the server refuses the call without them anyway.
 * The writers get the Scripting task, due on that date.
 *
 * F-151: an OPTIONAL target release day under the script date. It seeds the
 * Projects page's overlay (the board never stores it), so it is sent only
 * when set — a create without one is byte-for-byte the F-135 body — and it
 * can never precede the script date (the server refuses that too).
 */
function LongFormForm({
  onBack,
  onClose,
  onCreate,
}: {
  onBack: () => void;
  onClose: () => void;
  onCreate: (input: CreateBoardRequest, attachment?: AgentAttachment, sourcePath?: string) => void;
}) {
  const [scriptFinishDate, setScriptFinishDate] = useState("");
  const [targetReleaseDate, setTargetReleaseDate] = useState("");
  const [attachment, setAttachment] = useState<AgentAttachment | null>(null);
  // F-147: a picked file names the board; typing after that still wins.
  const nameField = useBoardNameField("", attachment);
  const name = nameField.value;
  const [createChatRoom, setCreateChatRoom] = useState(() => defaultCreateChatRoom("long"));
  const { team, setTeam, selected, toggle } = useWriters();

  // Keys compare lexically by design (date-key.ts), so `>=` is date order.
  // The picker's `min` already keeps the target on or after the script date;
  // this also covers the fallback plain field, where a typed date ignores it.
  const targetOk =
    targetReleaseDate === "" ||
    (isCalendarDateKey(targetReleaseDate) && targetReleaseDate >= scriptFinishDate);
  const ready =
    name.trim().length > 0 && isCalendarDateKey(scriptFinishDate) && selected.size > 0 && targetOk;

  const changeScriptFinishDate = (next: string) => {
    setScriptFinishDate(next);
    // Moving the script past the target drops the target rather than
    // flagging it: the old target was planned against the old script date,
    // and an optional field left blank is a valid create, a red one is not.
    if (targetReleaseDate !== "" && next !== "" && targetReleaseDate < next) {
      setTargetReleaseDate("");
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    onCreate(
      {
        name: name.trim(),
        scriptFinishDate,
        assignees: assigneesFrom(team, selected),
        createChatRoom,
        ...(targetReleaseDate !== "" ? { targetReleaseDate } : {}),
      },
      attachment ?? undefined,
    );
  };

  return (
    <form onSubmit={submit} className="assign-dialog create-board-form">
      <label className="create-board-field">
        <span className="create-board-label">Name</span>
        <input
          className="text-input"
          autoFocus
          placeholder={nameField.naming ? "Naming it after the file…" : "Board name"}
          value={name}
          onChange={(event) => nameField.setValue(event.target.value)}
          maxLength={500}
        />
      </label>
      <div className="create-board-field">
        <span className="create-board-label" id="create-board-date-label">
          Script due
        </span>
        {/* F-137: a button, not a date field. An empty `<input type="date">`
            shows "dd/mm/yyyy" and a calendar glyph — three placeholder
            segments that read as a half-filled form, in a separator order
            that is wrong for half the people who see it. */}
        <DateButton
          id="create-board-date"
          value={scriptFinishDate}
          onChange={changeScriptFinishDate}
          placeholder="Set script due date"
          min={todayDateKey()}
        />
      </div>
      <div className="create-board-field">
        <span className="create-board-label">Target release</span>
        <div className="create-board-date-row">
          <DateButton
            id="create-board-release"
            value={targetReleaseDate}
            onChange={setTargetReleaseDate}
            placeholder="Set target release (optional)"
            min={scriptFinishDate || todayDateKey()}
          />
          {/* The DateButton has no clear of its own (every other date it
              holds is required); an optional one needs a way back to none. */}
          {targetReleaseDate !== "" && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              aria-label="Clear the target release date"
              onClick={() => setTargetReleaseDate("")}
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="create-board-field">
        <span className="create-board-label">Who writes the script?</span>
        <RosterPicker selected={selected} onToggle={toggle} onLoaded={setTeam} />
      </div>
      <StoryboardFilePicker attachment={attachment} onChange={setAttachment} />
      <ChatRoomCheckbox checked={createChatRoom} onChange={setCreateChatRoom} />
      <p className="assign-dialog-lead create-board-hint">
        A Scripting task due on that date is created for them on the task board.
      </p>
      <div className="modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}>
          Back
        </button>
        <span className="spacer" />
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={!ready}>
          {selected.size > 1 ? `Create & assign (${selected.size})` : "Create & assign"}
        </button>
      </div>
    </form>
  );
}

/** A date the user chose over the plan, or null while the plan stands. */
interface ShortsOverrides {
  scriptFinishDate: string | null;
  editDueDate: string | null;
  releases: Array<string | null>;
}

const NO_OVERRIDES: ShortsOverrides = {
  scriptFinishDate: null,
  editDueDate: null,
  releases: Array.from({ length: SHORTS_PER_BOARD }, () => null),
};

/**
 * A Short (F-140; one per board since F-143): ONE date field — the shoot
 * day, next Monday by default — and the plan it implies drawn as a strip.
 * Every derived date is editable under "Adjust dates", for the week the rule
 * gets wrong (a holiday Monday, a Thursday shoot); moving the shoot day
 * re-flows the plan and drops the adjustments, because they were made
 * against the old week. The dates are sent explicitly, so what the strip
 * showed is what the board gets even when the server's plan rule is newer
 * than this client's.
 */
function ShortForm({
  initialShootDate,
  onBack,
  onClose,
  onCreate,
}: {
  /** F-141: "Plan next week" hands in the next Short's shoot day. */
  initialShootDate?: string;
  onBack: () => void;
  onClose: () => void;
  onCreate: (input: CreateBoardRequest, attachment?: AgentAttachment, sourcePath?: string) => void;
}) {
  const [shootDate, setShootDate] = useState(() => initialShootDate ?? nextMondayKey(todayDateKey()));
  const [overrides, setOverrides] = useState<ShortsOverrides>(NO_OVERRIDES);
  const [adjusting, setAdjusting] = useState(false);
  const { team, setTeam, selected, toggle, prefilled } = useWriters("shorts");
  const [attachment, setAttachment] = useState<AgentAttachment | null>(null);
  /** Where a file from Local Files sits — the board keeps it on row 1 (F-155). */
  const [sourcePath, setSourcePath] = useState<string | null>(null);
  const [createChatRoom, setCreateChatRoom] = useState(() => defaultCreateChatRoom("shorts"));
  // The default follows the shoot day until a file or the user names it (F-147);
  // a file from Local Files names it as it is, extension dropped (F-155).
  const nameField = useBoardNameField(shortBoardName(shootDate), attachment, {
    fileNameOnly: sourcePath !== null,
  });
  const name = nameField.value;

  const plan = useMemo(() => shortsPlan(shootDate), [shootDate]);
  const scriptFinishDate = overrides.scriptFinishDate ?? plan.scriptFinishDate;
  const editDueDate = overrides.editDueDate ?? plan.editDueDate;
  const releases = plan.releaseDates.map((planned, i) => overrides.releases[i] ?? planned);
  const hasOverrides =
    overrides.scriptFinishDate !== null ||
    overrides.editDueDate !== null ||
    overrides.releases.some((d) => d !== null);
  const release = releases[0] ?? plan.releaseDates[0] ?? "";

  const changeShootDate = (next: string) => {
    if (!isCalendarDateKey(next)) return;
    setShootDate(next);
    setOverrides(NO_OVERRIDES);
  };

  const setRelease = (index: number, next: string) => {
    setOverrides((prev) => {
      const list = [...prev.releases];
      list[index] = next === "" ? null : next;
      return { ...prev, releases: list };
    });
  };

  const datesValid =
    isCalendarDateKey(shootDate) &&
    isCalendarDateKey(scriptFinishDate) &&
    isCalendarDateKey(editDueDate) &&
    releases.every(isCalendarDateKey);
  const ready = name.trim().length > 0 && datesValid && selected.size > 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    onCreate(
      {
        name: name.trim(),
        kind: "shorts",
        shootDate,
        scriptFinishDate,
        editDueDate,
        shorts: releases.map((releaseDate, i) => ({ slot: i + 1, releaseDate })),
        assignees: assigneesFrom(team, selected),
        createChatRoom,
      },
      attachment ?? undefined,
      attachment !== null && sourcePath !== null ? sourcePath : undefined,
    );
  };

  const when = (key: string) => formatDateKeyWithWeekday(key);

  return (
    <form onSubmit={submit} className="assign-dialog create-board-form">
      <div className="create-board-field">
        <span className="create-board-label">Shoot day</span>
        <DateButton
          id="create-shorts-shoot"
          value={shootDate}
          onChange={changeShootDate}
          placeholder="Set the shoot day"
          prefix="Shoot"
          min={todayDateKey()}
        />
      </div>
      <ol className="shorts-timeline" aria-label="The Short's plan">
        <li>
          <span className="shorts-timeline-when">{when(scriptFinishDate)}</span>
          <span className="shorts-timeline-what">Script due</span>
        </li>
        <li className="shorts-timeline-anchor">
          <span className="shorts-timeline-when">{when(shootDate)}</span>
          <span className="shorts-timeline-what">Shoot</span>
        </li>
        <li>
          <span className="shorts-timeline-when">{when(editDueDate)}</span>
          <span className="shorts-timeline-what">Edit due</span>
        </li>
        <li>
          <span className="shorts-timeline-when">{when(release)}</span>
          <span className="shorts-timeline-what">Release</span>
        </li>
      </ol>
      <label className="create-board-field">
        <span className="create-board-label">Name</span>
        <input
          className="text-input"
          placeholder={nameField.naming ? "Naming it after the file…" : undefined}
          value={name}
          onChange={(event) => nameField.setValue(event.target.value)}
          maxLength={500}
        />
      </label>
      <div className="create-board-field">
        <span className="create-board-label">Who writes the script?</span>
        <RosterPicker selected={selected} onToggle={toggle} onLoaded={setTeam} />
        {prefilled && (
          <span className="create-board-hint assign-dialog-lead">
            Prefilled with the Shorts crew&apos;s writers.
          </span>
        )}
      </div>
      <div className="shorts-adjust">
        <button
          type="button"
          className="btn btn-ghost shorts-adjust-toggle"
          aria-expanded={adjusting}
          onClick={() => setAdjusting((open) => !open)}
        >
          {adjusting ? "▾" : "▸"} Adjust dates
          {hasOverrides && !adjusting ? " · adjusted" : ""}
        </button>
        {adjusting && (
          <>
            <div className="shorts-adjust-grid">
              <div className="create-board-field">
                <span className="create-board-label">Script due</span>
                <DateButton
                  value={scriptFinishDate}
                  onChange={(next) =>
                    setOverrides((prev) => ({ ...prev, scriptFinishDate: next || null }))
                  }
                  placeholder="Set script due"
                />
              </div>
              <div className="create-board-field">
                <span className="create-board-label">Edit due</span>
                <DateButton
                  value={editDueDate}
                  onChange={(next) => setOverrides((prev) => ({ ...prev, editDueDate: next || null }))}
                  placeholder="Set edit due"
                />
              </div>
              {releases.map((releaseDate, i) => (
                <div className="create-board-field" key={i}>
                  <span className="create-board-label">Release</span>
                  <DateButton
                    value={releaseDate}
                    onChange={(next) => setRelease(i, next)}
                    placeholder="Set release day"
                  />
                </div>
              ))}
            </div>
            {hasOverrides && (
              <div className="shorts-adjust-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setOverrides(NO_OVERRIDES)}>
                  Reset to plan
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <StoryboardFilePicker
        attachment={attachment}
        onChange={(picked, hostPath) => {
          setAttachment(picked);
          setSourcePath(picked !== null && hostPath !== undefined ? hostPath : null);
        }}
        kind="shorts"
      />
      <ChatRoomCheckbox checked={createChatRoom} onChange={setCreateChatRoom} />
      <p className="assign-dialog-lead create-board-hint">
        A Scripting task due {formatDateKey(scriptFinishDate)} is created for them on the task board.
      </p>
      <div className="modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}>
          Back
        </button>
        <span className="spacer" />
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={!ready}>
          {selected.size > 1 ? `Create & assign (${selected.size})` : "Create & assign"}
        </button>
      </div>
    </form>
  );
}

/**
 * F-141: what the calendar owes this week, from the boards' own dates — one
 * line above the kanban, day by day. This is the only reminder Kola has
 * (there are no date-driven notifications), so the dashboard is the
 * reminder, on the same reasoning as the Script-due chip. Nothing due this
 * week: nothing drawn.
 */
function ThisWeekStrip({ boards, onOpen }: { boards: BoardSummary[]; onOpen: (boardId: string) => void }) {
  const entries = thisWeekEntries(boards, todayDateKey());
  if (entries.length === 0) return null;
  const byDay = new Map<string, WeekEntry[]>();
  for (const entry of entries) {
    const list = byDay.get(entry.dateKey) ?? [];
    list.push(entry);
    byDay.set(entry.dateKey, list);
  }
  return (
    <div className="this-week" role="list" aria-label="This week">
      <span className="this-week-title">This week</span>
      {[...byDay.entries()].map(([dateKey, list]) => (
        <span key={dateKey} className="this-week-day" role="listitem">
          <span className="this-week-when">{formatDateKeyWithWeekday(dateKey)}</span>
          {list.map((entry) => (
            <button
              key={`${entry.boardId}-${entry.label}`}
              type="button"
              className={`this-week-entry${entry.overdue ? " this-week-overdue" : ""}`}
              onClick={() => onOpen(entry.boardId)}
              title={`${entry.label} · ${entry.boardName}`}
            >
              <span className="this-week-label">{entry.label}</span>
              <span className="this-week-board">{entry.boardName}</span>
            </button>
          ))}
        </span>
      ))}
    </div>
  );
}

/** "Archived" ticked: the boards nobody sees on the kanban, each with Restore. */
function ArchivedList({
  boards,
  emptyNote,
  onOpen,
  onRestore,
}: {
  boards: BoardSummary[];
  emptyNote: string;
  onOpen: (board: BoardSummary) => void;
  onRestore: (board: BoardSummary) => void;
}) {
  return (
    <div className="dashboard-archived">
    <table className="board-table" aria-label="Archived boards">
      <thead>
        <tr>
          <th>Name</th>
          <th style={{ width: 180 }}>Archived</th>
          <th style={{ width: 120 }} />
        </tr>
      </thead>
      <tbody>
        {boards.length === 0 && (
          <tr>
            <td colSpan={3} className="empty-state">
              {emptyNote}
            </td>
          </tr>
        )}
        {boards.map((board) => (
          <tr key={board.id}>
            <td>
              <span
                className="board-name-link"
                role="link"
                tabIndex={0}
                onClick={() => onOpen(board)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") onOpen(board);
                }}
              >
                {/* F-148: the kind leads the row. */}
                <KindBadge kind={board.kind} slots={board.shorts.length} />
                {board.name}
                <span className={`status-chip status-${board.status}`}>
                  {BOARD_STATUS_LABELS[board.status]}
                </span>
              </span>
            </td>
            <td>{formatDate(board.archivedAt ?? board.createdAt)}</td>
            <td>
              <div className="board-row-actions">
                <button type="button" className="btn" onClick={() => onRestore(board)}>
                  Restore
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
